import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    ColorDropdownComponent,
    ProjectColors,
} from '@connect-services/client/components/color-dropdown/color-dropdown.component';
import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { Pin } from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { PinService } from '@connect-services/client/services/pin.service';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { SnackBarService, SnackBarTypeEnum } from '@shared/client/services/snack-bar.service';

export type ManagePinModalData = { coordinates: [number, number]; pin?: never } | { coordinates?: never; pin: Pin };

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ColorDropdownComponent, ComplexModalTemplate, RessourcesModule],
    selector: 'app-manage-pin-modal',
    templateUrl: './manage-pin-modal.component.html',
})
export class ManagePinModalComponent {
    protected readonly createForm = new FormGroup<{
        color: FormControl<ProjectColors>;
        description: FormControl<string>;
        isShared: FormControl<boolean>;
        name: FormControl<string>;
    }>({
        color: new FormControl(ProjectColors.LIGHT_BLUE, {
            nonNullable: true,
            validators: [Validators.pattern(/^#[0-9a-fA-F]{6}$/)],
        }),
        description: new FormControl('', { nonNullable: true }),
        isShared: new FormControl(false, { nonNullable: true }),
        name: new FormControl('', {
            nonNullable: true,
            validators: [Validators.maxLength(255), Validators.required],
        }),
    });
    protected data: ManagePinModalData = inject(MAT_DIALOG_DATA);
    protected readonly isSaving = signal<boolean>(false);

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly dialogRef = inject(MatDialogRef<ManagePinModalComponent>);
    private readonly pinService = inject(PinService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly snackBarService = inject(SnackBarService);

    public constructor() {
        const projectId = this.projectBloc.value()?.id;

        this.populateForm();

        effect(() => {
            const currentProjectId = this.projectBloc.value()?.id;
            if (!this.pinService.isActive() || !this.pinService.canManage() || currentProjectId !== projectId) {
                this.dialogRef.close();
            }
        });
    }

    public setPin(pin: Pin): boolean {
        if (this.isSaving()) {
            return false;
        }

        this.data = { pin };
        this.populateForm();
        this.createForm.markAsPristine();
        this.createForm.markAsUntouched();
        this.changeDetectorRef.markForCheck();

        return true;
    }

    protected close(): void {
        this.dialogRef.close();
    }

    protected onModalResult(result: ModalResultEnum): void {
        if (result === ModalResultEnum.CANCEL) {
            this.close();

            return;
        }

        void this.save();
    }

    protected async save(): Promise<void> {
        if (this.createForm.invalid || this.isSaving()) {
            return;
        }

        const value = this.createForm.getRawValue();

        this.isSaving.set(true);
        try {
            if (this.data.pin) {
                await this.pinService.update(this.data.pin.id, {
                    color: value.color,
                    description: value.description || null,
                    isShared: value.isShared,
                    name: value.name,
                });
            } else {
                await this.pinService.create(
                    {
                        color: value.color,
                        description: value.description || undefined,
                        isShared: value.isShared,
                        name: value.name,
                    },
                    this.data.coordinates,
                );
            }
        } catch (error: unknown) {
            if (error instanceof HttpErrorResponse) {
                return;
            }

            if (error instanceof Error) {
                this.snackBarService.openPlainTextSnackBar(error.message, SnackBarTypeEnum.ERROR);

                return;
            }
        } finally {
            this.dialogRef.close();
            this.isSaving.set(false);
        }
    }

    private populateForm(): void {
        if (!this.data.pin) {
            return;
        }

        const pinColor = this.data.pin.color;
        AssertService.isEnum<ProjectColors>(pinColor, ProjectColors);

        this.createForm.setValue({
            color: pinColor,
            description: this.data.pin.description ?? '',
            isShared: this.data.pin.isShared,
            name: this.data.pin.name,
        });
    }
}
